import React, { useMemo } from "react"
import PropTypes from "prop-types"
import DOMPurify from "dompurify"
import { Link } from "react-router-dom"
import { oldRoutes } from "router/old-routes"
import { Button, Flex } from "antd"
import { Dropdown, DropdownItem } from "components/common/Dropdown"
import Icon from "components/common/Icon"
import styles from "./index.module.css"

import { LazyLoadImage } from "react-lazy-load-image-component"
import { useTranslation } from "react-i18next"
import { useConfirmModal } from "modules/modals/hooks/useConfirmModal"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import moment from "moment"

import { useDispatch, useSelector } from "react-redux"
import { deletePost, publishPostToggle } from "store/posts"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const Post = ({ post, edit = false }) => {
  useLoaderWatchers({ watchers: ["posts.post"] })
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const confirmModal = useConfirmModal()
  const { role } = useSelector((state) => state.user)
  const { trips_collection } = useSelector((state) => state.posts.metadata)
  const { hasPermission } = usePermissions()
  const trip = useMemo(
    () => post?.trip || trips_collection?.find(({ id }) => id === +post.trip_id),
    [post.trip, post.trip_id, trips_collection]
  )
  const photos = useMemo(() => post?.photos?.filter(({ _destroy }) => !_destroy) || [], [post?.photos])
  const postStringTags = useMemo(
    () =>
      post.tags
        ?.filter(({ _destroy }) => !_destroy)
        .map(({ title }) => title)
        ?.join(" ") || "",
    [post.tags]
  )
  const isPiblished = post.id && post.status === "published"
  const isGuide = role === "guide_user"
  const canEditPost = isGuide || (role === "staff_user" && hasPermission(MODULES.MARKETING, ACTIONS.EDIT))

  const publishToggler = ({ currentTarget }) => dispatch(publishPostToggle(post.id, JSON.parse(currentTarget.value)))
  const deleteHandler = confirmModal(
    {
      title: "Are you sure you want to delete this post?",
      color: "danger",
      submitText: t("global.delete"),
      cancelText: t("global.cancel")
    },
    () => dispatch(deletePost(post.id))
  )

  return (
    <article className={styles.post}>
      <Flex align="center" gap={10} className={styles.postMeta}>
        {isPiblished || edit ? (
          <p className={styles.postTimestamp}>{moment(post.published_at).fromNow()}</p>
        ) : (
          canEditPost && (
            <Button type="primary" style={{ marginRight: "auto" }} size="small" onClick={publishToggler} value={true}>
              {t("global.publish")}
            </Button>
          )
        )}
        {post.id && !edit && canEditPost && (
          <Dropdown>
            <DropdownItem onClick={publishToggler} value={!isPiblished}>
              {t(`global.${isPiblished ? "unpublish" : "publish"}`)}
            </DropdownItem>
            <DropdownItem tag={Link} to={oldRoutes.guideMarketingPostEditPath({ id: post.id })}>
              {t("posts.edit")}
            </DropdownItem>
            <DropdownItem className="text-danger" onClick={deleteHandler}>
              {t("posts.delete")}
            </DropdownItem>
          </Dropdown>
        )}
      </Flex>

      <Flex align="flex-start" gap={10} wrap="wrap">
        <h2 className={styles.postTitle}>{post.title}</h2>
        <Flex align="center" gap={8} className={styles.postLocation}>
          <Icon iconName="Location" className="flex-shrink-0" size={28} block />
          {trip?.address || "—"}
        </Flex>
      </Flex>
      <hr className="my-15" />
      <div className="mb-15 text-prewrap" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post?.content || "") }} />
      {photos.length > 0 && (
        <div className="grid gap-2 mb-20">
          {photos.length === 1 && (
            <div className="g-col-12 g-col-lg-12" style={{ maxHeight: 550 }}>
              <LazyLoadImage
                src={photos.at(0).url || photos.at(0).file_preview}
                alt=""
                className="object-fit-cover w-100 h-100 rounded-1"
                wrapperClassName="w-100 h-100"
                effect="blur"
                placeholderSrc={photos.at(0).placeholder_url || null}
              />
            </div>
          )}

          {photos.length === 2 && (
            <>
              <div className="g-col-12 g-col-md-12 g-col-lg-6" style={{ maxHeight: 440 }}>
                <LazyLoadImage
                  src={photos.at(0).url || photos.at(0).file_preview}
                  alt=""
                  className="object-fit-cover w-100 h-100 rounded-1"
                  wrapperClassName="w-100 h-100"
                  effect="blur"
                  placeholderSrc={photos.at(0).placeholder_url || null}
                />
              </div>
              <div className="g-col-12 g-col-md-12 g-col-lg-6" style={{ maxHeight: 440 }}>
                <LazyLoadImage
                  src={photos.at(1).url || photos.at(1).file_preview}
                  alt=""
                  className="object-fit-cover w-100 h-100 rounded-1"
                  wrapperClassName="w-100 h-100"
                  effect="blur"
                  placeholderSrc={photos.at(1).placeholder_url || null}
                />
              </div>
            </>
          )}

          {photos.length === 3 && (
            <>
              <div className="g-col-12 g-col-lg-6" style={{ maxHeight: 560 }}>
                <LazyLoadImage
                  src={photos.at(0).url || photos.at(0).file_preview}
                  alt=""
                  className="object-fit-cover w-100 h-100 rounded-1"
                  wrapperClassName="w-100 h-100"
                  effect="blur"
                  placeholderSrc={photos.at(0).placeholder_url || null}
                />
              </div>
              <div className="g-col-12 g-col-lg-6 grid grid-cols-6 gap-2">
                <div className="g-col-6 grid grid-cols-1 grid-rows-8 gap-2" style={{ maxHeight: 560 }}>
                  <div className="g-row-4">
                    <LazyLoadImage
                      src={photos.at(1).url || photos.at(1).file_preview}
                      alt=""
                      className="object-fit-cover w-100 h-100 rounded-1"
                      wrapperClassName="w-100 h-100"
                      effect="blur"
                      placeholderSrc={photos.at(1).placeholder_url || null}
                    />
                  </div>
                  <div className="g-row-4">
                    <LazyLoadImage
                      src={photos.at(2).url || photos.at(2).file_preview}
                      alt=""
                      className="object-fit-cover w-100 h-100 rounded-1"
                      wrapperClassName="w-100 h-100"
                      effect="blur"
                      placeholderSrc={photos.at(2).placeholder_url || null}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {photos.length === 4 && (
            <>
              <div className="g-col-12 g-col-lg-6" style={{ maxHeight: 420 }}>
                <LazyLoadImage
                  src={photos.at(0).url || photos.at(0).file_preview}
                  alt=""
                  className="object-fit-cover w-100 h-100 rounded-1"
                  wrapperClassName="w-100 h-100"
                  effect="blur"
                  placeholderSrc={photos.at(0).placeholder_url || null}
                />
              </div>
              <div className="g-col-12 g-col-lg-6" style={{ maxHeight: 420 }}>
                <LazyLoadImage
                  src={photos.at(1).url || photos.at(1).file_preview}
                  alt=""
                  className="object-fit-cover w-100 h-100 rounded-1"
                  wrapperClassName="w-100 h-100"
                  effect="blur"
                  placeholderSrc={photos.at(1).placeholder_url || null}
                />
              </div>
              <div className="g-col-12 g-col-lg-6" style={{ maxHeight: 420 }}>
                <LazyLoadImage
                  src={photos.at(2).url || photos.at(2).file_preview}
                  alt=""
                  className="object-fit-cover w-100 h-100 rounded-1"
                  wrapperClassName="w-100 h-100"
                  effect="blur"
                  placeholderSrc={photos.at(2).placeholder_url || null}
                />
              </div>
              <div className="g-col-12 g-col-lg-6" style={{ maxHeight: 420 }}>
                <LazyLoadImage
                  src={photos.at(3).url || photos.at(3).file_preview}
                  alt=""
                  className="object-fit-cover w-100 h-100 rounded-1"
                  wrapperClassName="w-100 h-100"
                  effect="blur"
                  placeholderSrc={photos.at(3).placeholder_url || null}
                />
              </div>
            </>
          )}
        </div>
      )}
      <div className={styles.postTags}>{postStringTags}</div>
    </article>
  )
}

Post.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        file_preview: PropTypes.string,
        placeholder_url: PropTypes.string,
        _destroy: PropTypes.bool
      })
    ),
    trip_id: PropTypes.number,
    status: PropTypes.string,
    published_at: PropTypes.string,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        _destroy: PropTypes.bool
      })
    )
  }).isRequired,
  edit: PropTypes.bool
}

export default Post
